import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { OVERLAY_SLOTS, clamp, asNumber } from "./constants";

export function NumberedSlider({ label, testid, value, onChange, min = 0, max = 100 }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
        <span>{label}</span>
        <span className="tabular-nums">{Math.round(value)}</span>
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={1}
        onValueChange={(v) => onChange(v[0])}
        data-testid={testid}
      />
    </div>
  );
}

function AlignButtons({ testidBase, current, defaultAlign, onChange, hint }) {
  const active = (current || defaultAlign || "left").toLowerCase();
  return (
    <div className="flex items-center gap-1.5">
      <Label className="text-[11px] text-muted-foreground">Align</Label>
      <div className="inline-flex rounded-md border border-border overflow-hidden">
        {["left", "center", "right"].map((al) => (
          <button
            key={al}
            type="button"
            onClick={() => onChange(al)}
            className={`px-2 py-1 text-[11px] capitalize ${active === al ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted"}`}
            data-testid={`${testidBase}-align-${al}`}
          >
            {al}
          </button>
        ))}
      </div>
      {hint && (
        <span className="text-[10px] text-muted-foreground">
          X anchors {active === "left" ? "the start" : active === "center" ? "the centre" : "the end"} of the text.
        </span>
      )}
    </div>
  );
}

/**
 * Renders the sliders and alignment controls for a single overlay slot.
 * Behavior and ranges are preserved 1:1 with the original monolithic editor.
 */
export function OverlayElementRow({ slot, overlay, hasBackground, onChange }) {
  const s = slot;
  const o = overlay || s.defaults;
  const disabled = !o.visible;
  const isMainImage = s.key === "main_image";
  const noArt = isMainImage && !hasBackground;
  const hasColor = "color" in s.defaults && !s.noColor;
  const isZone = s.kind === "zone";
  const testidBase = `gc-overlay-${s.key}`;

  return (
    <div
      className="rounded-lg border border-border/60 px-3 py-2 space-y-2 bg-white"
      data-testid={testidBase}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium">{s.label}</p>
          {isMainImage && (
            <span className="text-[10px] rounded-full bg-muted text-muted-foreground border border-border px-2 py-0.5 uppercase tracking-wider">Layer&nbsp;0</span>
          )}
          {isZone && (
            <span className="text-[10px] rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-2 py-0.5 uppercase tracking-wider">Bounding&nbsp;box</span>
          )}
        </div>
        <Switch
          checked={!!o.visible}
          onCheckedChange={(v) => onChange({ visible: !!v })}
          data-testid={`${testidBase}-visible`}
        />
      </div>
      <div className={`${disabled || noArt ? "opacity-50 pointer-events-none" : ""}`}>
        {isZone ? (
          <>
            <div className="grid grid-cols-4 gap-2">
              <NumberedSlider label="X %"      testid={`${testidBase}-x`} value={asNumber(o.x, s.defaults.x)} onChange={(v) => onChange({ x: clamp(v, 0, 100) })} min={0} max={100} />
              <NumberedSlider label="Y %"      testid={`${testidBase}-y`} value={asNumber(o.y, s.defaults.y)} onChange={(v) => onChange({ y: clamp(v, 0, 100) })} min={0} max={100} />
              <NumberedSlider label="Width %"  testid={`${testidBase}-w`} value={asNumber(o.w, s.defaults.w)} onChange={(v) => onChange({ w: clamp(v, 10, 100) })} min={10} max={100} />
              <NumberedSlider label="Height %" testid={`${testidBase}-h`} value={asNumber(o.h, s.defaults.h)} onChange={(v) => onChange({ h: clamp(v, 4,  80) })} min={4}  max={80} />
            </div>
            <div className="grid grid-cols-1 mt-2">
              <NumberedSlider
                label="Size"
                testid={`${testidBase}-size`}
                value={asNumber(o.size, s.defaults.size)}
                onChange={(v) => onChange({ size: clamp(v, 4, 100) })}
                min={4}
                max={100}
              />
            </div>
          </>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <NumberedSlider label="X %" testid={`${testidBase}-x`} value={asNumber(o.x, s.defaults.x)} onChange={(v) => onChange({ x: clamp(v, 0, 100) })} min={0} max={100} />
            <NumberedSlider label="Y %" testid={`${testidBase}-y`} value={asNumber(o.y, s.defaults.y)} onChange={(v) => onChange({ y: clamp(v, 0, 100) })} min={0} max={100} />
            <NumberedSlider
              label="Size"
              testid={`${testidBase}-size`}
              value={asNumber(o.size, s.defaults.size)}
              onChange={(v) => onChange({ size: clamp(v, isMainImage ? 20 : 4, isMainImage ? 200 : 100) })}
              min={isMainImage ? 20 : 4}
              max={isMainImage ? 200 : 100}
            />
          </div>
        )}
      </div>
      {isMainImage && noArt && (
        <p className="text-[10px] text-muted-foreground italic">Upload a main image above to enable these controls.</p>
      )}
      {isZone && (
        <div className="flex items-center gap-3 flex-wrap">
          <AlignButtons
            testidBase={testidBase}
            current={o.align}
            defaultAlign={s.defaults.align}
            onChange={(al) => onChange({ align: al })}
          />
        </div>
      )}
      {s.kind === "text" && (
        <AlignButtons
          testidBase={testidBase}
          current={o.align}
          defaultAlign={s.defaults.align || "left"}
          onChange={(al) => onChange({ align: al })}
          hint
        />
      )}
      {hasColor && (
        <div className="flex items-center gap-2">
          <Label className="text-[11px] text-muted-foreground">Colour</Label>
          <Input
            type="color"
            className="w-16 h-8 p-0"
            value={o.color || s.defaults.color}
            onChange={(e) => onChange({ color: e.target.value })}
            data-testid={`${testidBase}-color`}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Full "Overlay Elements" panel — one row per slot. Kept as a plain
 * map over OVERLAY_SLOTS so the ordering and layout match the
 * original editor exactly.
 */
export default function CoordinateSliders({ overlays, hasBackground, setOverlay }) {
  return (
    <div className="rounded-xl border border-border p-3 space-y-3" data-testid="gc-overlay-controls">
      <p className="text-[11px] tracking-overline text-muted-foreground">OVERLAY ELEMENTS</p>
      {OVERLAY_SLOTS.map((s) => (
        <OverlayElementRow
          key={s.key}
          slot={s}
          overlay={overlays[s.key] || s.defaults}
          hasBackground={hasBackground}
          onChange={(patch) => setOverlay(s.key, patch)}
        />
      ))}
    </div>
  );
}
